// Public Booking Page — /book/:slug
//
// Zero-auth page. Sellers land here from a Jarvis SMS or from a link
// pasted anywhere. Design brief: minimal, warm, mobile-first. Every
// second of friction here is a lost lead — one page, three taps, done.
//
// Flow: date picker → duration chip → slot chip → contact form → confirm.

import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { addDays, format, isSameDay, startOfDay } from "date-fns";
import {
  Calendar as CalendarIcon, Check, ChevronLeft, ChevronRight,
  Clock, Loader2, MapPin, Sparkles, User,
} from "lucide-react";
import axios from "axios";
import SeminarCountdownChip from "@/components/v3/SeminarCountdownChip";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_BASE}/api`, timeout: 25000 });

function fmtLocalYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function PublicBookingPage() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [profileErr, setProfileErr] = useState(null);
  const [duration, setDuration]   = useState(30);
  const [date, setDate]           = useState(() => startOfDay(new Date()));
  const [slots, setSlots]         = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm]           = useState({ name: "", phone: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError]         = useState("");

  // Load profile once.
  useEffect(() => {
    let alive = true;
    api.get(`/public/book/${slug}`)
      .then((r) => { if (alive) { setProfile(r.data); setDuration(r.data.durations?.[0] || 30); } })
      .catch((e) => { if (alive) setProfileErr(e?.response?.data?.detail || "Booking page not found."); });
    return () => { alive = false; };
  }, [slug]);

  // Reload slots when date or duration change.
  useEffect(() => {
    if (!profile) return;
    setSlotsLoading(true); setSlots([]); setSelectedSlot(null);
    api.get(`/public/book/${slug}/slots`, {
      params: { date: fmtLocalYMD(date), duration },
    })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [slug, date, duration, profile]);

  const submit = async () => {
    setError("");
    if (!selectedSlot) { setError("Please pick a time first."); return; }
    if (!form.name.trim()) { setError("Please tell us your name."); return; }
    if (!form.phone.trim() && !form.email.trim()) {
      setError("We need a phone or email to confirm.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/public/book/${slug}`, {
        start_iso:     selectedSlot.start_iso,
        duration_min:  duration,
        name:          form.name.trim(),
        phone:         form.phone.trim() || undefined,
        email:         form.email.trim() || undefined,
        notes:         form.notes.trim() || undefined,
      });
      setConfirmation({ ...res.data, slot: selectedSlot });
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  // 14-day date strip for quick nav.
  const dateStrip = useMemo(() => {
    return Array.from({ length: 14 }, (_, i) => addDays(startOfDay(new Date()), i));
  }, []);

  if (profileErr) {
    return (
      <div className="min-h-screen grid place-items-center px-6 bg-amber-50/40" data-testid="booking-not-found">
        <div className="max-w-md text-center">
          <div className="text-[11px] font-black uppercase tracking-widest text-amber-700">iSpeedToClose</div>
          <h1 className="mt-2 text-3xl font-black text-slate-900">Page not found</h1>
          <p className="mt-3 text-slate-600">{profileErr}</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-1 text-amber-700 font-semibold hover:underline">
            <ChevronLeft className="w-4 h-4" /> Back home
          </Link>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen grid place-items-center" data-testid="booking-loading">
        <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
      </div>
    );
  }

  if (confirmation) {
    return <ConfirmationView profile={profile} confirmation={confirmation} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50/50 via-white to-orange-50/30" data-testid="public-booking-page">
      {/* Header */}
      <header className="max-w-5xl mx-auto px-4 md:px-8 py-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {profile.logo ? (
            <img src={profile.logo} alt="" className="h-10 w-10 rounded-xl object-contain bg-white ring-1 ring-slate-200" />
          ) : (
            <div className="h-10 w-10 rounded-xl grid place-items-center text-white font-black shadow-sm"
                 style={{ backgroundColor: profile.accent || "#F59E0B" }}>
              {(profile.display_name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Book a call with</div>
            <div className="text-[17px] font-black text-slate-900 leading-tight">{profile.display_name}</div>
          </div>
        </div>
        <SeminarCountdownChip variant="warm" publicPage testid="book-page-seminar-countdown" />
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 pb-14">
        {/* Duration chips */}
        <section className="mt-2">
          <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 mb-2">
            How long do you need?
          </div>
          <div className="flex flex-wrap gap-2">
            {(profile.durations || [15, 30, 60]).map((d) => (
              <button key={d}
                onClick={() => setDuration(d)}
                data-testid={`booking-duration-${d}`}
                className={
                  "inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-[13.5px] font-bold transition-all " +
                  (d === duration
                    ? "border-amber-600 bg-amber-600 text-white shadow-sm"
                    : "border-slate-200 bg-white text-slate-700 hover:border-amber-300")
                }>
                <Clock className="w-3.5 h-3.5" /> {d} min
              </button>
            ))}
          </div>
        </section>

        <section className="mt-8">
          <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 mb-2">
            Which day?
          </div>
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
            {dateStrip.map((d) => {
              const active = isSameDay(d, date);
              return (
                <button key={d.toISOString()}
                  onClick={() => setDate(d)}
                  data-testid={`booking-date-${format(d, "yyyy-MM-dd")}`}
                  className={
                    "flex-shrink-0 w-16 py-2 rounded-xl border text-center transition-all " +
                    (active
                      ? "border-amber-600 bg-amber-50 shadow-sm"
                      : "border-slate-200 bg-white hover:border-amber-300")
                  }>
                  <div className={"text-[10.5px] font-bold uppercase tracking-widest " + (active ? "text-amber-700" : "text-slate-400")}>
                    {format(d, "EEE")}
                  </div>
                  <div className={"text-[18px] font-black leading-tight " + (active ? "text-amber-900" : "text-slate-800")}>
                    {format(d, "d")}
                  </div>
                  <div className={"text-[9px] font-bold uppercase " + (active ? "text-amber-700" : "text-slate-400")}>
                    {format(d, "MMM")}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Slots */}
        <section className="mt-6">
          <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 mb-2">
            Available times — {format(date, "EEEE, MMMM d")}
          </div>
          {slotsLoading ? (
            <div className="flex items-center gap-2 text-[13px] text-slate-500 py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Finding open times…
            </div>
          ) : slots.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/40 p-6 text-center">
              <CalendarIcon className="w-6 h-6 text-slate-400 mx-auto" />
              <div className="mt-2 text-[13px] font-semibold text-slate-700">Fully booked or a day off</div>
              <div className="mt-1 text-[11.5px] text-slate-500">Try another day above.</div>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-1.5" data-testid="booking-slots-grid">
              {slots.map((s) => {
                const active = selectedSlot?.start_iso === s.start_iso;
                return (
                  <button key={s.start_iso}
                    onClick={() => setSelectedSlot(s)}
                    data-testid={`booking-slot-${s.start_iso}`}
                    className={
                      "px-2 py-2 rounded-lg border text-[13px] font-semibold transition-all " +
                      (active
                        ? "border-amber-600 bg-amber-600 text-white shadow-sm"
                        : "border-slate-200 bg-white text-slate-800 hover:border-amber-300 hover:bg-amber-50/40")
                    }>
                    {s.label}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Contact form */}
        {selectedSlot && (
          <section className="mt-8 rounded-2xl border border-amber-200 bg-white p-5 shadow-sm max-w-xl"
                   data-testid="booking-contact-form">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-700">
              <Sparkles className="w-3.5 h-3.5" />
              Your info
            </div>
            <div className="mt-1 text-[13.5px] text-slate-600 leading-snug">
              Booking <b>{format(new Date(selectedSlot.start_iso), "EEE MMM d")}</b> at <b>{selectedSlot.label}</b> · {duration} min
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Name *</span>
                <input value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                       data-testid="booking-name"
                       placeholder="Your name"
                       className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </label>
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Phone</span>
                <input value={form.phone}
                       onChange={(e) => setForm({ ...form, phone: e.target.value })}
                       data-testid="booking-phone"
                       placeholder="+15551234567"
                       className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Email</span>
                <input type="email" value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })}
                       data-testid="booking-email"
                       placeholder="you@example.com"
                       className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  Anything I should know? (property address, ballpark price, etc.)
                </span>
                <textarea value={form.notes}
                          onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          data-testid="booking-notes"
                          rows={2}
                          className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </label>
            </div>
            {error && (
              <div className="mt-3 rounded-md bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 text-[12.5px]" data-testid="booking-error">
                {error}
              </div>
            )}
            <div className="mt-4 flex flex-col sm:flex-row-reverse gap-2">
              <button onClick={submit}
                      disabled={submitting}
                      data-testid="booking-submit"
                      className="inline-flex items-center justify-center gap-2 rounded-lg text-white text-[13.5px] font-bold px-5 py-2.5 shadow-md hover:opacity-90 disabled:opacity-60"
                      style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {submitting ? "Confirming…" : "Confirm booking"}
              </button>
              <button onClick={() => setSelectedSlot(null)}
                      className="text-[13px] text-slate-500 hover:text-slate-800 px-3 py-2.5">
                Pick a different time
              </button>
            </div>
          </section>
        )}

        <footer className="mt-14 pt-6 border-t border-slate-200 text-center text-[11px] text-slate-500">
          Powered by <a href="/" className="font-bold text-amber-700 hover:underline">iSpeedToClose</a> — the operating system for real estate investors.
        </footer>
      </main>
    </div>
  );
}

function ConfirmationView({ profile, confirmation }) {
  const dt = new Date(confirmation.confirmed_at);
  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-amber-50 via-white to-orange-50 px-4"
         data-testid="booking-confirmation">
      <div className="max-w-md w-full text-center rounded-3xl bg-white border border-amber-200 shadow-lg p-8">
        <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500 grid place-items-center shadow-lg">
          <Check className="w-8 h-8 text-white" />
        </div>
        <h1 className="mt-5 text-3xl font-black text-slate-900">You're booked!</h1>
        <p className="mt-2 text-[15px] text-slate-600 leading-snug">
          {format(dt, "EEEE, MMMM d")} at{" "}
          <b>{format(dt, "h:mm a")}</b>
          <span className="text-slate-400"> ({confirmation.duration_min} min)</span>
        </p>
        <div className="mt-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-left text-[13px] text-slate-700 leading-snug">
          <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-amber-700">
            <MapPin className="w-3 h-3" /> What's next
          </div>
          <ul className="mt-2 space-y-1.5">
            <li className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" /> Confirmation SMS just went out to your phone.</li>
            <li className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" /> {profile.persona_name || "Jordan"} will call you at the time above.</li>
            <li className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" /> Reply to the SMS if anything changes.</li>
          </ul>
        </div>
        <div className="mt-6 flex items-center justify-center gap-1.5 text-[11.5px] text-slate-500">
          <User className="w-3 h-3" /> Talking to <b className="text-slate-700">{profile.operator_name || profile.display_name}</b>
        </div>
      </div>
    </div>
  );
}
